import React, { useCallback, useMemo } from 'react'
import { Box, Checkbox, Flex } from '@workwhile/ui'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { ColumnDef } from '@tanstack/react-table'
import { CompumaticPunch } from 'api/admin/timeClock/listCompumaticPunches'
import { ExpandablePaginatedTable } from 'components/Table/ExpandablePaginatedTable'
import { useCompumaticClockPunchesContextValue } from '../context/CompumaticClockPunchesProvider'
import {
  CompumaticCompanyLocationCell,
  CompumaticPunchCell,
  CompumaticShiftCell,
  CompumaticStatusCell,
  CompumaticWorkerCell,
} from './CompumaticPunchCells'
import { RelatedCompumaticPunchesProvider } from './RelatedCompumaticPunches/context/RelatedCompumaticPunchesProvider'
import { RelatedCompumaticPunchesMain } from './RelatedCompumaticPunches/components/RelatedCompumaticPunchesMain'
import { CompumaticPunchActionsCell } from './CompumaticPunchActionsCell'

const stopRowExpand = (event: React.SyntheticEvent) => {
  event.stopPropagation()
}

const EXPAND_CHEVRON_SIZE = 20

const SelectPunchCheckbox = ({
  checked,
  label,
  onChange,
}: {
  checked: boolean
  label: string
  onChange: () => void
}) => (
  <Box onClick={stopRowExpand} onKeyDown={stopRowExpand}>
    <Checkbox value={checked} onChange={onChange} aria-label={label} />
  </Box>
)

export const CompumaticClockPunchesTable = () => {
  const {
    state: { data, pagination, isFetching, selectedPunchIds },
    actions: {
      setPagination,
      togglePunchSelected,
      selectPunchIds,
      deselectPunchIds,
    },
  } = useCompumaticClockPunchesContextValue()

  const currentPagePunchIds = useMemo(
    () => data.items.map((punch) => punch.id),
    [data.items]
  )

  const allOnPageSelected =
    currentPagePunchIds.length > 0 &&
    currentPagePunchIds.every((id) => selectedPunchIds.includes(id))

  const toggleSelectAllOnPage = useCallback(() => {
    if (allOnPageSelected) {
      deselectPunchIds(currentPagePunchIds)
      return
    }
    selectPunchIds(currentPagePunchIds)
  }, [allOnPageSelected, currentPagePunchIds, deselectPunchIds, selectPunchIds])

  const columns = useMemo<ColumnDef<CompumaticPunch>[]>(
    () => [
      {
        id: 'select',
        size: 48,
        header: () => (
          <SelectPunchCheckbox
            checked={allOnPageSelected}
            label="Select all punches on this page"
            onChange={toggleSelectAllOnPage}
          />
        ),
        cell: ({ row }) => (
          <SelectPunchCheckbox
            checked={selectedPunchIds.includes(row.original.id)}
            label={`Select punch ${row.original.id}`}
            onChange={() => togglePunchSelected(row.original.id)}
          />
        ),
      },
      {
        id: 'expand',
        size: 40,
        header: () => null,
        cell: ({ row }) => (
          <Flex
            alignItems="center"
            justifyContent="center"
            color="lightText"
            role="button"
            tabIndex={0}
            aria-expanded={row.getIsExpanded()}
            aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
            onClick={(event) => {
              event.stopPropagation()
              row.toggleExpanded()
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                event.stopPropagation()
                row.toggleExpanded()
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            {row.getIsExpanded() ? (
              <ChevronDown size={EXPAND_CHEVRON_SIZE} />
            ) : (
              <ChevronRight size={EXPAND_CHEVRON_SIZE} />
            )}
          </Flex>
        ),
      },
      {
        header: 'Company',
        accessorKey: 'company',
        minSize: 200,
        cell: ({ row }) => (
          <CompumaticCompanyLocationCell punch={row.original} />
        ),
      },
      {
        header: 'Worker',
        accessorKey: 'worker',
        minSize: 160,
        cell: ({ row }) => <CompumaticWorkerCell punch={row.original} />,
      },
      {
        header: 'Punch',
        accessorKey: 'punchAt',
        minSize: 200,
        cell: ({ row }) => <CompumaticPunchCell punch={row.original} />,
      },
      {
        header: 'Status',
        accessorKey: 'status',
        minSize: 140,
        cell: ({ row }) => <CompumaticStatusCell punch={row.original} />,
      },
      {
        header: 'Shift',
        accessorKey: 'shift',
        minSize: 200,
        cell: ({ row }) => <CompumaticShiftCell punch={row.original} />,
      },
      {
        header: 'Actions',
        id: 'actions',
        size: 72,
        cell: ({ row }) => <CompumaticPunchActionsCell punch={row.original} />,
      },
    ],
    [
      allOnPageSelected,
      selectedPunchIds,
      togglePunchSelected,
      toggleSelectAllOnPage,
    ]
  )

  return (
    <ExpandablePaginatedTable
      columns={columns}
      items={data.items}
      rowCount={data.totalRows}
      pagination={pagination}
      setPagination={setPagination}
      isFetching={isFetching}
      emptyMessage="No Compumatic clock punches found for the current filters."
      emptyStateMaxWidth={['100%', '100%', '50%', '50%']}
      getRowCanExpand={() => true}
      showExpandColumn={false}
      expandOnRowClick
      renderExpandedContent={(punch) => (
        <RelatedCompumaticPunchesProvider punchId={punch.id}>
          <RelatedCompumaticPunchesMain />
        </RelatedCompumaticPunchesProvider>
      )}
    />
  )
}
